import React, { Fragment } from 'react';
import { Link, NavLink, Route, Routes } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import cn from 'classnames';
import { HiOutlineX } from 'react-icons/hi';
import { AiFillCaretDown } from 'react-icons/ai';
import logo from 'assets/images/icons/logo_white.svg';
import { isUndefined, startCase } from 'lodash';
import PropTypes from 'prop-types';
import { useRoutePermission } from 'modules/auth/hooks';

function NavItem({ label, icon: Icon, to, withNested }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `hover:bg-blue/60 flex items-center justify-between px-3  py-2  transition-all ${
          isActive
            ? ' bg-white text-primary pointer-events-none'
            : 'drop-shadow-sm text-white'
        } `
      }
      startswith="true"
    >
      <div className="flex items-end gap-2">
        {Icon && <Icon className="text-2xl" />}
        <span className="capitalize font-bold tracking-wide text-[0.9rem]">
          {label}
        </span>
      </div>
      {withNested && <AiFillCaretDown />}
    </NavLink>
  );
}

NavItem.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.instanceOf(Object).isRequired,
  to: PropTypes.string.isRequired,
  withNested: PropTypes.bool.isRequired,
};

function NavSubItem({ navs }) {
  return (
    <div className="bg-white">
      {navs.map((nav) => (
        <NavLink
          key={nav?.path}
          to={nav?.path}
          className={({ isActive }) =>
            `flex items-center pl-11 py-2 space-x-2 opacity-80 transition-all duration-200 ${
              isActive
                ? ' text-primary-700 drop-shadow-sm'
                : ' text-primary hover:bg-blue/70 hover:text-white'
            } `
          }
        >
          <span className="capitalize font-semibold tracking-wide text-[0.9rem]">
            {startCase(nav?.path)}
          </span>
        </NavLink>
      ))}
    </div>
  );
}

NavSubItem.propTypes = {
  navs: PropTypes.instanceOf(Array).isRequired,
};

function Navigation({ className }) {
  const routes = useRoutePermission();
  return (
    <nav className={cn('w-full', className)}>
      <ul className="space-y-3">
        {routes.map((route) =>
          route.subRoutes ? (
            <div key={route.path}>
              <NavItem
                to={`/${route.path}`}
                label={startCase(route.path)}
                icon={route.icon}
                withNested
              />
              <Routes>
                <Route
                  path={`${route.path}/*`}
                  element={
                    <NavSubItem
                      navs={route.subRoutes.filter((x) =>
                        isUndefined(x?.index),
                      )}
                    />
                  }
                />
              </Routes>
            </div>
          ) : (
            <NavItem
              to={`/${route.path}`}
              label={startCase(route.path)}
              icon={route.icon}
              key={route.path}
              withNested={false}
            />
          ),
        )}
      </ul>
    </nav>
  );
}

Navigation.defaultProps = {
  className: '',
};

Navigation.propTypes = {
  className: PropTypes.string,
};

function Sidebar({ isOpen, setIsOpen }) {
  const onClose = () => setIsOpen(false);
  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 flex z-50"
          open
          onClose={onClose}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={onClose}
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative w-[280px] flex flex-col bg-gradient-to-b from-primary-500 to-primary-600">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={onClose}
                    type="button"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <HiOutlineX
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </Transition.Child>
              <div className="px-3 py-5">
                <Link to="/">
                  <img className="h-12 w-auto" src={logo} alt="Brand" />
                </Link>
              </div>
              <div className="h-full overflow-y-auto">
                <Navigation />
              </div>

              <div className="pb-6 px-4 text-[12px] text-sm text-white/90 tracking-wide text-center font-thin">
                <div>TxtBox Core</div>
                <div>Copyright 2019 | v0.2.0</div>
              </div>
            </div>
          </Transition.Child>
          <div className="flex-shrink-0 w-14" aria-hidden="true" />
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="z-40 flex-shrink-0 hidden xl:flex flex-col justify-between h-full w-[230px] bg-gradient-to-b from-primary-500 to-primary-600 shadow-2xl">
        <div className="pt-10">
          <Navigation />
        </div>
        <div className="pb-6 px-4 text-[12px] text-white/90 tracking-wide text-center font-thin">
          <div>TxtBox Core</div>
          <div>Copyright 2019 | v0.2.0</div>
        </div>
      </div>
    </>
  );
}

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default Sidebar;
